import { jsPDF } from 'jspdf';
import autoTable from 'jspdf-autotable';

const TEAL = [120, 194, 173]; // #78C2AD
const TEAL_DARK = [45, 122, 102];
const TEXT = [33, 37, 41];
const MUTED = [108, 117, 125];
const LIGHT_GRAY = [245, 247, 248];
const ROW_ALT = [250, 250, 250];
const BORDER = [220, 226, 230];

const COMPANY = {
  name: 'SOQUIMAT',
  legalName: 'SOQUIMAT SpA',
  tagline: 'Soluciones Químicas y Materiales',
  rut: '77.112.762-2',
  billingAddress: 'Aníbal Pinto #340 (facturación)',
  city: 'Concepción, Región del Biobío, Chile.',
  web: 'www.soquimat.cl',
  shipAddress: 'Jaime Repullo #1294, Talcahuano (entregas)',
  shipCity: 'Concepción, Región del Biobío, Chile.',
  shipPhone: '+56 9 3946 6150',
};

const loadLogoDataUrl = () =>
  new Promise((resolve) => {
    const image = new Image();
    image.onload = () => {
      try {
        const canvas = document.createElement('canvas');
        canvas.width = image.width || 220;
        canvas.height = image.height || 90;
        const context = canvas.getContext('2d');
        if (!context) {
          resolve(null);
          return;
        }
        context.drawImage(image, 0, 0);
        resolve(canvas.toDataURL('image/png'));
      } catch {
        resolve(null);
      }
    };
    image.onerror = () => resolve(null);
    image.src = '/assets/soquimat-logo.png';
  });

const pad2 = (n) => String(n).padStart(2, '0');

const parseLocalDate = (raw) => {
  const s = String(raw || '').trim().slice(0, 10);
  const m = s.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (!m) return null;
  return new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
};

const formatDateDdMmYyyy = (raw) => {
  const d = parseLocalDate(raw) || new Date();
  if (Number.isNaN(d.getTime())) return '—';
  return `${pad2(d.getDate())}/${pad2(d.getMonth() + 1)}/${d.getFullYear()}`;
};

const formatDateDdMmYy = (raw) => {
  const d = parseLocalDate(raw) || new Date();
  if (Number.isNaN(d.getTime())) return '01-01-00';
  return `${pad2(d.getDate())}-${pad2(d.getMonth() + 1)}-${pad2(d.getFullYear() % 100)}`;
};

const yearYy = (raw) => {
  const d = parseLocalDate(raw) || new Date();
  return pad2(d.getFullYear() % 100);
};

/** Extrae el correlativo YYXXX (o YYXXX-…) de un po_code. */
export const resolvePoCorrelative = (poCode = '', orderDate = '') => {
  const raw = String(poCode || '').trim();
  const digits = raw.replace(/[^0-9]/g, '');
  if (/^\d{5,}(?:-|$)/.test(raw) || /^\d{5,}$/.test(digits)) {
    // Preferir el stem YYXXX (5 dígitos) antes de sufijos -vN / -01
    const stem = raw.match(/^(\d{5,})/);
    if (stem) return stem[1];
    return digits.slice(0, Math.max(5, digits.length));
  }
  if (/^\d{1,3}$/.test(digits)) {
    return `${yearYy(orderDate)}${digits.padStart(3, '0')}`;
  }
  if (digits) return digits;
  return `${yearYy(orderDate)}000`;
};

/**
 * Nombre por defecto: YYZZZ.OC (DD-MM-YY).pdf
 * ZZZ = correlativo de la OC (ej. 26482 → 26482.OC (14-08-26).pdf)
 */
export function buildPurchaseOrderPdfFilename({ po_code = '', order_date = '' } = {}) {
  const correlative = resolvePoCorrelative(po_code, order_date);
  const datePart = formatDateDdMmYy(order_date);
  return `${correlative}.OC (${datePart}).pdf`;
}

const formatMoneyCl = (value, { decimals = null } = {}) => {
  const n = Number(value || 0);
  if (!Number.isFinite(n)) return '0';
  const abs = Math.abs(n);
  const useDecimals =
    decimals != null ? decimals : Math.abs(n - Math.round(n)) > 0.001 ? 1 : 0;
  const fixed = abs.toFixed(useDecimals);
  const [intPart, decPart] = fixed.split('.');
  const withDots = intPart.replace(/\B(?=(\d{3})+(?!\d))/g, '.');
  const signed = n < 0 ? `-${withDots}` : withDots;
  return decPart != null ? `${signed},${decPart}` : signed;
};

const taxRateForDocument = (documentType) => {
  const label = String(documentType || '').toLowerCase();
  if (label.includes('exent') || label.includes('guia') || label.includes('guía')) return 0;
  return 0.19;
};

const drawFilledRect = (doc, x, y, w, h, rgb) => {
  doc.setFillColor(...rgb);
  doc.rect(x, y, w, h, 'F');
};

const drawLabelValueBlock = (doc, x, y, width, rows, { labelWidth = 22 } = {}) => {
  let cursor = y;
  doc.setFontSize(8);
  rows.forEach(([label, value]) => {
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(...TEXT);
    doc.text(`${label} :`, x, cursor);
    doc.setFont('helvetica', 'normal');
    const text = String(value || '—');
    const lines = doc.splitTextToSize(text, width - labelWidth - 2);
    doc.text(lines, x + labelWidth, cursor);
    cursor += Math.max(4.2, lines.length * 3.6);
  });
  return cursor;
};

/**
 * Genera el PDF de Orden de Compra (layout tipo imagen corporativa).
 * @returns {Promise<jsPDF>}
 */
export async function buildPurchaseOrderPdf(input = {}) {
  const orderDate = String(input.order_date || '').slice(0, 10);
  const poCode = resolvePoCorrelative(input.po_code, orderDate);
  const currency = String(input.currency || 'CLP').toUpperCase() || 'CLP';
  const currencyLabel = `$ ${currency}`;
  const items = Array.isArray(input.items) ? input.items : [];
  const exchangeRate = Number(input.exchange_rate || 1) || 1;
  const taxRate = taxRateForDocument(input.document_type);

  const lineRows = items
    .map((item, idx) => {
      const qty = Number(item.quantity || 0);
      const unitPriceOrig = Number(item.unit_price || 0);
      const unitPrice = Math.round(unitPriceOrig * exchangeRate * 1000) / 1000;
      const total = Math.round(qty * unitPrice * 100) / 100;
      const description = [item.description, item.detail].map((x) => String(x || '').trim()).filter(Boolean).join(' — ');
      return {
        index: idx + 1,
        description: description || '—',
        quantity: qty,
        unitPrice,
        total,
      };
    })
    .filter((row) => row.description !== '—' || row.quantity > 0 || row.unitPrice > 0);

  const subTotal = Math.round(lineRows.reduce((s, r) => s + r.total, 0) * 100) / 100;
  const totalNeto = Math.round(subTotal);
  const iva = Math.round(totalNeto * taxRate);
  const totalBruto = totalNeto + iva;
  const retention = Math.round(Number(input.withholdings_amount || 0));
  const advance = Math.round(Number(input.advance_amount || 0));
  const amountDue = Math.max(0, totalBruto - retention - advance);

  const vendorName = String(input.vendor_contact_name || '').trim() || '0';
  const vendorCompany = String(input.vendor_company || input.supplier_name || '').trim() || '—';
  const vendorRut = String(input.vendor_rut || input.supplier_rut || '').trim() || '—';
  const vendorAddress = String(input.vendor_address || input.supplier_address || '').trim() || '—';
  const vendorPhone = String(input.vendor_phone || '').trim() || '0';

  const shipName = String(input.buyer_name || '').trim() || '—';
  const shipAddress = String(input.delivery_place || '').trim() || COMPANY.shipAddress;
  const paymentTerms = String(input.payment_terms || '').trim() || 'N/A';
  const shipMethod = String(input.delivery_conditions || '').trim() || 'N/A';
  const shipConditions = 'N/A';
  const authorizedBy = String(input.authorized_by || input.buyer_name || '').trim() || '—';
  const comments =
    String(input.comments || '').trim() ||
    '- Agregar por favor a su factura el Nº de Orden de Compra.';

  const doc = new jsPDF({ unit: 'mm', format: 'a4' });
  const pageWidth = doc.internal.pageSize.getWidth();
  const marginX = 12;
  const contentWidth = pageWidth - marginX * 2;
  let y = 10;

  // Página X de Y (se actualiza al final)
  doc.setFontSize(8);
  doc.setTextColor(...MUTED);
  doc.text('Página 1 de 1', pageWidth - marginX, 8, { align: 'right' });

  const logo = await loadLogoDataUrl();
  if (logo) {
    try {
      doc.addImage(logo, 'PNG', marginX, y, 14, 14);
    } catch {
      /* ignore */
    }
  }

  doc.setTextColor(...TEXT);
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(16);
  doc.text(COMPANY.name, marginX + 16, y + 6);
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(7.5);
  doc.setTextColor(...MUTED);
  doc.text(COMPANY.tagline, marginX + 16, y + 10.5);
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(9);
  doc.setTextColor(...TEXT);
  doc.text(COMPANY.legalName, marginX + 16, y + 15);
  doc.setTextColor(...TEAL_DARK);
  doc.text(COMPANY.rut, marginX + 16 + doc.getTextWidth(`${COMPANY.legalName}  `), y + 15);

  // Badge ORDEN DE COMPRA
  const badgeW = 48;
  const badgeH = 16;
  const badgeX = pageWidth - marginX - badgeW;
  drawFilledRect(doc, badgeX, y, badgeW, badgeH, TEAL);
  doc.setTextColor(255, 255, 255);
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(10);
  doc.text('ORDEN DE COMPRA', badgeX + badgeW / 2, y + 6.2, { align: 'center' });
  doc.setFontSize(12);
  doc.text(`Nº ${poCode}`, badgeX + badgeW / 2, y + 12.5, { align: 'center' });

  y += 22;

  const metaLeft = [
    ['Autorizado por', authorizedBy],
    ['Dirección', COMPANY.billingAddress],
    ['Ciudad', COMPANY.city],
    ['Web', COMPANY.web],
  ];
  const metaRight = [
    ['Fecha', formatDateDdMmYyyy(orderDate)],
    ['O.C #', poCode],
  ];
  const metaY = y;
  drawLabelValueBlock(doc, marginX, metaY, contentWidth * 0.58, metaLeft, { labelWidth: 28 });
  drawLabelValueBlock(doc, marginX + contentWidth * 0.62, metaY, contentWidth * 0.38, metaRight, {
    labelWidth: 16,
  });
  y = metaY + 20;

  // VENDEDOR / ENVÍE A
  const colGap = 4;
  const colW = (contentWidth - colGap) / 2;
  const boxHeaderH = 6.5;
  const boxBodyH = 28;

  const drawInfoBox = (x, title, rows) => {
    drawFilledRect(doc, x, y, colW, boxHeaderH, TEAL);
    doc.setTextColor(255, 255, 255);
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(9);
    doc.text(title, x + 2.5, y + 4.5);
    doc.setDrawColor(...BORDER);
    doc.setFillColor(255, 255, 255);
    doc.rect(x, y + boxHeaderH, colW, boxBodyH, 'FD');
    drawLabelValueBlock(doc, x + 2.5, y + boxHeaderH + 4.5, colW - 5, rows, { labelWidth: 20 });
  };

  drawInfoBox(marginX, 'VENDEDOR', [
    ['Nombre', vendorName],
    ['Empresa', vendorCompany],
    ['RUT', vendorRut],
    ['Dirección', vendorAddress],
    ['Teléfono', vendorPhone],
  ]);
  drawInfoBox(marginX + colW + colGap, 'ENVÍE A', [
    ['Nombre', shipName],
    ['Empresa', COMPANY.legalName],
    ['Dirección', shipAddress],
    ['Ciudad', COMPANY.shipCity],
    ['Teléfono', COMPANY.shipPhone],
  ]);
  y += boxHeaderH + boxBodyH + 4;

  // Condiciones / método / pago
  const condW = contentWidth / 3;
  const condH = 12;
  [
    ['CONDICIONES DE ENVÍO', shipConditions],
    ['MÉTODO DE ENVÍO', shipMethod],
    ['FORMA DE PAGO', paymentTerms],
  ].forEach(([title, value], idx) => {
    const x = marginX + condW * idx;
    drawFilledRect(doc, x, y, condW, 5.5, TEAL);
    doc.setTextColor(255, 255, 255);
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(7.5);
    doc.text(title, x + condW / 2, y + 3.8, { align: 'center' });
    doc.setFillColor(...LIGHT_GRAY);
    doc.setDrawColor(...BORDER);
    doc.rect(x, y + 5.5, condW, condH - 5.5, 'FD');
    doc.setTextColor(...TEXT);
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(8);
    doc.text(String(value || 'N/A'), x + condW / 2, y + 10, { align: 'center' });
  });
  y += condH + 5;

  // Ítems
  autoTable(doc, {
    startY: y,
    margin: { left: marginX, right: marginX },
    head: [
      [
        'ITEM #',
        'DESCRIPCIÓN',
        'CANTIDAD',
        `PRECIO UNI. (${currencyLabel})`,
        `TOTAL (${currencyLabel})`,
      ],
    ],
    body: lineRows.length
      ? lineRows.map((row) => [
          String(row.index),
          row.description,
          formatMoneyCl(row.quantity, { decimals: 1 }),
          formatMoneyCl(row.unitPrice, { decimals: 1 }),
          formatMoneyCl(row.total, { decimals: 1 }),
        ])
      : [['—', 'Sin ítems', '—', '—', '—']],
    styles: {
      font: 'helvetica',
      fontSize: 8,
      textColor: TEXT,
      lineColor: BORDER,
      lineWidth: 0.2,
      cellPadding: 2.2,
      valign: 'middle',
    },
    headStyles: {
      fillColor: TEAL,
      textColor: [255, 255, 255],
      fontStyle: 'bold',
      halign: 'center',
    },
    columnStyles: {
      0: { cellWidth: 16, halign: 'center' },
      1: { cellWidth: 'auto' },
      2: { cellWidth: 22, halign: 'right' },
      3: { cellWidth: 32, halign: 'right' },
      4: { cellWidth: 30, halign: 'right', fillColor: LIGHT_GRAY },
    },
    alternateRowStyles: { fillColor: ROW_ALT },
    theme: 'grid',
  });

  y = (doc.lastAutoTable?.finalY || y) + 6;

  // Comentarios + totales
  const totalsW = 72;
  const commentsW = contentWidth - totalsW - 4;
  const totalsRows = [
    [`Sub Total (${currencyLabel})`, formatMoneyCl(subTotal, { decimals: 1 })],
    [`Total Neto (${currencyLabel})`, formatMoneyCl(totalNeto)],
    [`IVA (${currencyLabel})`, formatMoneyCl(iva)],
    [`Total Bruto (${currencyLabel})`, formatMoneyCl(totalBruto)],
    ['Retención', formatMoneyCl(retention)],
    ['Abono / Anticipo', formatMoneyCl(advance)],
    [`TOTAL A PAGAR (${currencyLabel})`, `$ ${formatMoneyCl(amountDue)}`],
  ];
  const rowH = 5.8;
  const totalsH = totalsRows.length * rowH;
  const commentsH = Math.max(totalsH, 28);

  drawFilledRect(doc, marginX, y, commentsW, 5.5, TEAL);
  doc.setTextColor(255, 255, 255);
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(8);
  doc.text('Comentarios o instrucciones especiales', marginX + 2.5, y + 3.8);
  doc.setFillColor(...LIGHT_GRAY);
  doc.setDrawColor(...BORDER);
  doc.rect(marginX, y + 5.5, commentsW, commentsH - 5.5, 'FD');
  doc.setTextColor(...TEXT);
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(8);
  const commentLines = doc.splitTextToSize(comments, commentsW - 5);
  doc.text(commentLines, marginX + 2.5, y + 10);

  const totalsX = marginX + commentsW + 4;
  totalsRows.forEach(([label, value], idx) => {
    const rowY = y + idx * rowH;
    const isLast = idx === totalsRows.length - 1;
    doc.setFillColor(...LIGHT_GRAY);
    doc.setDrawColor(...BORDER);
    doc.rect(totalsX, rowY, totalsW, rowH, 'FD');
    doc.setTextColor(...TEXT);
    doc.setFont('helvetica', isLast ? 'bold' : 'normal');
    doc.setFontSize(isLast ? 8.5 : 7.5);
    doc.text(label, totalsX + 2, rowY + 3.9);
    doc.text(value, totalsX + totalsW - 2, rowY + 3.9, { align: 'right' });
    if (isLast) {
      doc.setDrawColor(...TEXT);
      doc.setLineWidth(0.4);
      doc.line(totalsX, rowY, totalsX + totalsW, rowY);
      doc.setLineWidth(0.2);
    }
  });

  y += Math.max(commentsH, totalsH) + 14;

  // Firmas
  const sigW = contentWidth / 3;
  ['Autorizado por', 'Fecha', 'Firma'].forEach((label, idx) => {
    const x = marginX + sigW * idx + 8;
    const lineW = sigW - 16;
    doc.setDrawColor(...TEXT);
    doc.setLineWidth(0.35);
    doc.line(x, y, x + lineW, y);
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(8);
    doc.setTextColor(...TEXT);
    doc.text(label, x + lineW / 2, y + 5, { align: 'center' });
  });

  // Numeración de páginas
  const pageCount = doc.getNumberOfPages();
  for (let i = 1; i <= pageCount; i += 1) {
    doc.setPage(i);
    doc.setFontSize(8);
    doc.setTextColor(...MUTED);
    doc.text(`Página ${i} de ${pageCount}`, pageWidth - marginX, 8, { align: 'right' });
  }

  return doc;
}

export async function downloadPurchaseOrderPdf(input = {}) {
  const doc = await buildPurchaseOrderPdf(input);
  const filename = buildPurchaseOrderPdfFilename({
    po_code: input.po_code,
    order_date: input.order_date,
  });
  doc.save(filename);
  return filename;
}
